import { useEffect, useState } from "react";

/**
 * Global Trade Network — professional animated visualization
 * showing live route arcs between JMR hubs.
 */

// Hub coordinates within an 1100x500 viewBox (lon/lat → flat projection).
const HUBS = [
  { id: "chennai", label: "Chennai", x: 770, y: 320, primary: true },
  { id: "london", label: "London", x: 525, y: 165 },
  { id: "dubai", label: "Dubai", x: 670, y: 270 },
  { id: "singapore", label: "Singapore", x: 870, y: 360 },
  { id: "shanghai", label: "Shanghai", x: 910, y: 250 },
  { id: "rotterdam", label: "Rotterdam", x: 540, y: 155 },
  { id: "newyork", label: "New York", x: 290, y: 230 },
  { id: "losangeles", label: "Los Angeles", x: 175, y: 250 },
  { id: "lagos", label: "Lagos", x: 535, y: 340 },
  { id: "sydney", label: "Sydney", x: 985, y: 445 },
];

// Trade lanes (curved arcs between hubs)
const LANES = [
  { from: "chennai", to: "london", delay: 0 },
  { from: "chennai", to: "dubai", delay: 0.6 },
  { from: "chennai", to: "singapore", delay: 1.2 },
  { from: "dubai", to: "rotterdam", delay: 1.8 },
  { from: "london", to: "newyork", delay: 0.3 },
  { from: "shanghai", to: "losangeles", delay: 0.9 },
  { from: "singapore", to: "sydney", delay: 1.5 },
  { from: "lagos", to: "rotterdam", delay: 2.1 },
  { from: "newyork", to: "rotterdam", delay: 1.0 },
  { from: "chennai", to: "lagos", delay: 1.7 },
];

function arcPath(a, b) {
  // Quadratic curve that bows upward (toward y = small)
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy);
  // Perpendicular offset for the curve apex
  const offset = Math.min(110, dist * 0.32);
  // Normal vector (rotated 90deg ccw) so arc bows upward
  const nx = -dy / dist;
  const ny = dx / dist;
  const cx = mx + nx * offset;
  const cy = my + ny * offset;
  return { d: `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`, length: dist };
}

const STATS = [
  { k: "100+", v: "Trade Lanes" },
  { k: "24/7", v: "Operations" },
  { k: "3", v: "Continents" },
  { k: "<60s", v: "Quote Response" },
];

export default function FreightAnimation() {
  const [now, setNow] = useState("");
  useEffect(() => {
    const tick = () =>
      setNow(
        new Date().toUTCString().replace("GMT", "UTC")
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const hubMap = Object.fromEntries(HUBS.map((h) => [h.id, h]));

  return (
    <section
      data-testid="freight-animation"
      aria-label="Global trade network"
      className="relative w-full overflow-hidden bg-blue-950 border-t border-white/10"
    >
      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Radial highlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 60% 50%, rgba(14,165,233,0.10), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="relative inline-flex w-2.5 h-2.5">
                <span className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-60" />
                <span className="relative rounded-full w-2.5 h-2.5 bg-sky-400" />
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400">
                Live · JMR Global Trade Network
              </span>
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white leading-[1.05]">
              Moving cargo across<br />
              <span className="text-sky-400">every meridian.</span>
            </h2>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/40">System Time</p>
            <p className="font-mono text-xs md:text-sm text-white/80 mt-1">{now}</p>
          </div>
        </div>

        {/* Map */}
        <div className="relative">
          <svg
            viewBox="0 0 1100 500"
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto"
            role="img"
            aria-label="Global trade lanes visualization"
          >
            <defs>
              <linearGradient id="laneGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0" />
                <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
              </linearGradient>
              <radialGradient id="hubGlow">
                <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
              </radialGradient>
              <pattern id="dots" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1" fill="rgba(56,189,248,0.18)" />
              </pattern>
            </defs>

            {/* Stylized continent silhouettes via dot pattern shapes (abstract). */}
            <g opacity="0.55">
              {/* Eurasia/Africa block */}
              <path
                d="M430,140 Q520,90 620,120 Q740,90 880,140 Q960,180 960,260 Q900,320 820,310
                   Q740,330 700,300 Q650,360 590,340 Q540,380 510,330 Q450,360 440,300
                   Q400,260 430,140 Z"
                fill="url(#dots)"
              />
              {/* Africa */}
              <path
                d="M510,330 Q560,360 580,420 Q540,460 510,420 Q480,380 510,330 Z"
                fill="url(#dots)"
              />
              {/* Americas */}
              <path
                d="M120,180 Q200,140 290,180 Q330,220 310,280 Q280,330 240,300
                   Q200,360 230,420 Q200,440 180,400 Q160,360 180,320
                   Q140,300 130,250 Q100,210 120,180 Z"
                fill="url(#dots)"
              />
              {/* Australia */}
              <path
                d="M930,420 Q990,400 1010,440 Q990,470 940,460 Q920,445 930,420 Z"
                fill="url(#dots)"
              />
            </g>

            {/* Lanes */}
            <g>
              {LANES.map((lane, i) => {
                const a = hubMap[lane.from];
                const b = hubMap[lane.to];
                if (!a || !b) return null;
                const { d } = arcPath(a, b);
                return (
                  <g key={`${lane.from}-${lane.to}-${i}`}>
                    {/* Faint base */}
                    <path d={d} fill="none" stroke="rgba(56,189,248,0.18)" strokeWidth="1" />
                    {/* Drawing pulse along path */}
                    <path
                      d={d}
                      fill="none"
                      stroke="url(#laneGrad)"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      style={{
                        strokeDasharray: "60 600",
                        strokeDashoffset: 660,
                        animation: `lane-pulse 5.5s ${lane.delay}s linear infinite`,
                      }}
                    />
                  </g>
                );
              })}
            </g>

            {/* Hubs */}
            <g>
              {HUBS.map((h) => (
                <g key={h.id}>
                  {/* outer glow */}
                  <circle cx={h.x} cy={h.y} r={h.primary ? 18 : 12} fill="url(#hubGlow)" />
                  {/* ring */}
                  <circle
                    cx={h.x}
                    cy={h.y}
                    r={h.primary ? 6 : 4}
                    fill="none"
                    stroke="#38bdf8"
                    strokeWidth="1.2"
                    style={{
                      transformOrigin: `${h.x}px ${h.y}px`,
                      animation: `hub-ring 3s ${(h.x % 5) * 0.3}s ease-out infinite`,
                    }}
                  />
                  {/* dot */}
                  <circle
                    cx={h.x}
                    cy={h.y}
                    r={h.primary ? 3 : 2}
                    fill={h.primary ? "#7dd3fc" : "#38bdf8"}
                  />
                  {/* label */}
                  <text
                    x={h.x + 9}
                    y={h.y + 3}
                    fill="#cbd5e1"
                    fontSize="10"
                    fontFamily="'IBM Plex Mono', monospace"
                    style={{ letterSpacing: "0.05em" }}
                  >
                    {h.label.toUpperCase()}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </div>

        {/* Stats footer */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10 mt-12">
          {STATS.map((s, i) => (
            <div
              key={s.v}
              className={`py-6 ${i < STATS.length - 1 ? "md:border-r" : ""} ${i % 2 === 0 ? "border-r md:border-r" : ""} border-white/10`}
            >
              <div className="font-display font-black text-3xl md:text-4xl text-white tracking-tighter mb-1">
                {s.k}
              </div>
              <div className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/50">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
